import { Component } from 'react';
import {
  SimpleDate,
  equals,
  fromString,
  getBoundedDate,
  isValid,
  nextDay,
  prevDay,
  toFormattedString,
} from '../../utils/SimpleDate';
import { SyntheticEvent, syntheticEvent } from '../../utils/SyntheticEvent';
import { callIfPossible } from '../../utils/functions';

export const getPageFromDate = (date?: SimpleDateType) => {
  if (!isValid(date)) return null;
  return { year: date?.year, month: date?.month };
};

interface SimpleDateType {
  year: number;
  month: number;
  date: number;
}

export interface AbstractCalendarProps {
  /**
   * The year and month currently displayed by the calendar.
   * @tags State
   */
  calendarPage?: {
    year: number;
    month: number;
  };
  /**
   * Render function that receives the input handlers and the formatted input value.
   * @tags Customization
   */
  children: (props: {
    onBlur: () => void;
    onInputChange: (evt: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (evt: React.KeyboardEvent<HTMLInputElement>) => void;
    onKeyUp: () => void;
    inputValue: string;
  }) => React.ReactNode;
  /**
   * Dates that cannot be selected.
   * @tags Data
   */
  disabledValues?: SimpleDateType[];
  /**
   * The format used to display and parse the date in the input.
   * @tags Form
   */
  format?: 'L' | 'YYYY-MM-DD' | 'LL' | 'll';
  /**
   * The latest selectable date.
   * @tags Form
   */
  max?: SimpleDateType;
  /**
   * The latest selectable date, used when `max` is not set.
   * @tags Form
   */
  maxValue?: SimpleDateType;
  /**
   * The earliest selectable date.
   * @tags Form
   */
  min?: SimpleDateType;
  /**
   * The earliest selectable date, used when `min` is not set.
   * @tags Form
   */
  minValue?: SimpleDateType;
  /**
   * Callback when the displayed calendar page changes.
   * @tags Events
   */
  onCalendarPageChange?: (
    event: SyntheticEvent<{ year: number; month: number }>
  ) => void;
  /**
   * Callback when the selected date changes.
   * @tags Events
   */
  onChange?: (event: SyntheticEvent<SimpleDate>) => void;
  /**
   * Callback when the focused date changes.
   * @tags Events
   */
  onFocusedValueChange?: (event: SyntheticEvent<SimpleDate>) => void;
  /**
   * Callback when the calendar opens or closes.
   * @tags Events
   */
  onOpenChange?: (open: SyntheticEvent<boolean>) => void;
  /**
   * Whether the calendar is open.
   * @tags State
   */
  open?: boolean;
  /**
   * The date the calendar starts on when no value is selected.
   * @tags Data
   */
  originDate?: SimpleDateType;
  /**
   * The date treated as today.
   * @tags Data
   */
  today?: SimpleDateType;
  /**
   * The selected date.
   * @tags Form
   */
  value?: SimpleDateType;
}

interface AbstractCalendarState {
  inputValue: string;
}

class AbstractCalendar extends Component<
  AbstractCalendarProps,
  AbstractCalendarState
> {
  private _keyUpTimeout;
  private _pressedKey: string | null;

  constructor(props: AbstractCalendarProps) {
    super(props);
    this.state = {
      inputValue: toFormattedString(props.value, props.format),
    };
    this._keyUpTimeout = 0;
    this._pressedKey = null;
  }

  componentDidUpdate(prevProps: AbstractCalendarProps) {
    const {
      calendarPage,
      onCalendarPageChange,
      onOpenChange,
      open,
      value,
      format = 'L',
    } = prevProps;
    if (!equals(value, this.props.value)) {
      // Flip to the calendar page that includes the selected date
      const newCalendarPage = getPageFromDate(this.props.value);
      if (newCalendarPage && !equals(newCalendarPage, calendarPage)) {
        callIfPossible(onCalendarPageChange, syntheticEvent(newCalendarPage));
      }

      // `pressedKey` tells us whether this change was caused by the mouse or the keyboard
      const shouldClosePicker = this._pressedKey === null;
      const shouldUpdateInput =
        this._pressedKey === null ||
        this._pressedKey === 'ArrowUp' ||
        this._pressedKey === 'ArrowDown';

      if (shouldClosePicker && open && onOpenChange) {
        onOpenChange(syntheticEvent(false));
      }

      if (shouldUpdateInput) {
        this.updateInput(this.props.value, this.props.format);
      }
    } else if (format !== this.props.format) {
      this.updateInput(this.props.value, this.props.format);
    }
  }

  componentWillUnmount() {
    clearTimeout(this._keyUpTimeout);
  }

  updateInput(value?: SimpleDate, format?: string) {
    const { inputValue } = this.state;
    const formattedDate = toFormattedString(value, format);
    if (formattedDate !== inputValue) {
      this.setState({ inputValue: formattedDate });
    }
  }

  updateDate(newValue?: SimpleDate) {
    const {
      calendarPage,
      disabledValues,
      max,
      maxValue,
      min,
      minValue,
      onCalendarPageChange,
      onChange,
      onFocusedValueChange,
      onOpenChange,
    } = this.props;
    if (!calendarPage || newValue?.month !== calendarPage.month) {
      callIfPossible(
        onCalendarPageChange,
        syntheticEvent({
          year: newValue?.year,
          month: newValue?.month,
        })
      );
    }
    if (
      !equals(
        newValue,
        getBoundedDate(newValue, min || minValue, max || maxValue)
      ) ||
      (disabledValues &&
        disabledValues.some((disabledValue) => equals(newValue, disabledValue)))
    ) {
      // Show tooltip on disabled date
      callIfPossible(onFocusedValueChange, syntheticEvent(newValue));
      if (onOpenChange) {
        onOpenChange(syntheticEvent(true));
      }
      return;
    }
    callIfPossible(onFocusedValueChange, syntheticEvent(null));
    callIfPossible(onChange, syntheticEvent(newValue));
  }

  handleInputChange = (evt: any) => {
    const { format = 'L', onChange, value } = this.props;
    this.setState({ inputValue: evt.target.value });

    // Update date value as the user types
    const userInput = evt.target.value.trim();
    if (userInput === '') {
      callIfPossible(onChange, syntheticEvent(null));
    } else {
      const userInputDate = fromString(userInput, format);
      if (userInputDate !== null && !equals(userInputDate, value)) {
        this.updateDate(userInputDate);
      }
    }
  };

  handleKeyDown = (evt: any) => {
    const { onOpenChange, open, originDate, today, value } = this.props;
    const { key } = evt;

    clearTimeout(this._keyUpTimeout);
    this._pressedKey = key;

    // The "keyboard cursor" is implicitly on the current selection, or today's date if clear.
    const computedCurrentDate = isValid(value) ? value : originDate || today;

    if (key === 'ArrowUp') {
      evt.preventDefault();
      const newValue = nextDay(computedCurrentDate);
      this.updateDate(newValue);
    } else if (key === 'ArrowDown') {
      evt.preventDefault();
      const newValue = prevDay(computedCurrentDate);
      this.updateDate(newValue);
    } else if (key === 'Escape') {
      if (!open || !onOpenChange) return;
      evt.preventDefault();
      evt.stopPropagation();
      onOpenChange(syntheticEvent(false));
    }
  };

  handleKeyUp = () => {
    // The delay here should ensure that onKeyUp resolves after onChange, even under IE11 (#7331).
    clearTimeout(this._keyUpTimeout);
    this._keyUpTimeout = setTimeout(() => {
      this._pressedKey = null;
    }, 0);
  };

  handleBlur = () => {
    const { format = 'L', onFocusedValueChange, value } = this.props;
    this.updateInput(value, format);
    this._pressedKey = null;
    callIfPossible(onFocusedValueChange, syntheticEvent(null));
  };

  render() {
    const { children } = this.props;
    const { inputValue } = this.state;
    return children({
      onBlur: this.handleBlur,
      onInputChange: this.handleInputChange,
      onKeyDown: this.handleKeyDown,
      onKeyUp: this.handleKeyUp,
      inputValue,
    });
  }
}

export default AbstractCalendar;
